import React, { FunctionComponent, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import _get from 'lodash/get';

import { verifyUPI, updateTransaction } from '../promiseAction';
import { processTransaction } from '../promiseAction';
import { parsePrice, formatPrice } from '../../../utils/utils';
import { showLoader } from '../../Loader/action';
import { redirectToBankPage } from '../processTransactionResponse';
import { sendEvent2 } from '../../../utils/tracking';

const Form: FunctionComponent<{
  paymentInfo: any;
  amount: number;
  type: string;
  updateInternetCharges: Function;
  internetCharges: number;
  fromFastag: any;
}> = ({ paymentInfo, amount, type, updateInternetCharges, internetCharges, fromFastag }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, errors } = useForm();
  const fAmount = formatPrice(amount);

  const [error, setError] = useState('');

  const dispatchLoader = useCallback((payload: any) => dispatch(showLoader(payload)), [dispatch]);

  const dispatchProcessTransaction = useCallback(
    (payload: any) =>
      dispatch(processTransaction(payload)).then((resp: any) => {
        const response = _get(resp, 'data.body.resultInfo', {});
        const resultInfo = _get(resp, 'data.body.resultInfo', {});
        if (response.resultStatus === 'S') {
          redirectToBankPage(payload.paymentMode, resp);
        } else {
          if (resultInfo && resultInfo.resultMsg) {
            setError(resultInfo.resultMsg);
          } else {
            setError('Something went wrong');
          }
        }
        dispatchLoader({ show: false });
      }),
    [dispatch, dispatchLoader],
  );

  const dispatchUpdateTransaction = useCallback(
    (payload: any) =>
      dispatch(updateTransaction(payload.params)).then((resp: any) => {
        if (resp.data.success) {
          const data: any = {
            requestType: 'NATIVE',
            mId: paymentInfo.merchantId,
            orderId: paymentInfo.transactionCode,
            paymentMode: 'UPI',
            channelCode: 'collect',
            payerAccount: payload.vpa,
            token: paymentInfo.checkSumHash,
          };
          dispatchProcessTransaction(data);
        } else {
          dispatchLoader({ show: false });
          setError(_get(resp, 'data.message', 'Something went wrong'));
        }
      }),
    [dispatch, paymentInfo, dispatchLoader, dispatchProcessTransaction],
  );

  const dispatchToStore = useCallback(
    (payload: any) =>
      dispatch(verifyUPI(payload)).then((resp: any) => {
        const valid = _get(resp, 'data.body.valid', false);
        const vpa = _get(resp, 'data.body.vpa', false);
        const resultInfo = _get(resp, 'data.body.resultInfo', false);
        if (valid) {
          setError('');
          const params = {
            appVersion: 'web',
            paymentMode: 'UPI',
            serviceProvider: 'PAYTM CUSTOM',
            userCode: paymentInfo.customerId,
            transactionCode: paymentInfo.transactionCode,
            transactionToken: paymentInfo.checkSumHash,
            amount: fAmount,
            pspAmount: parseFloat(formatPrice(internetCharges)),
          };
          dispatchUpdateTransaction({ params, vpa });
        } else {
          if (resultInfo && resultInfo.resultMsg) {
            setError(resultInfo.resultMsg);
          } else {
            setError('Something went wrong');
          }
          dispatchLoader({ show: false });
        }
      }),
    // TODO: handle dependencies 'fAmount', and 'internetCharges'
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, paymentInfo, dispatchLoader, dispatchUpdateTransaction],
  );

  const onSubmit = (data?: any) => {
    updateInternetCharges({ [type]: type });
    dispatchLoader({ show: true });
    dispatchToStore({
      id: data.id,
      mId: paymentInfo.merchantId,
      orderId: paymentInfo.transactionCode,
      token: paymentInfo.checkSumHash,
    });
    if (fromFastag?.fromFastag === true) {
      sendEvent2(
        'pay_btn',
        {
          event_action: 'click',
          screen_name: 'wallet_recharge_screen',
          miscellaneous: JSON.stringify({
            type: 'upi',
          }),
          target_product: 'fastag',
          event_platform: 'web',
        },
        true,
      );
    }
  };

  const handleChange = (event: any) => {
    setError('');
    if (!event.target.value) {
      updateInternetCharges({ [type]: '' });
    }
  };

  return (
    <div className="p16 mode-container">
      <form className="form-container pr8" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-content-s card-info">
          <div className={`info-column flex-1 ${errors.id ? 'error-field' : ''}`}>
            <p className="small mb8 font-light-grey form-label">UPI Address</p>
            <input
              type="text"
              placeholder=""
              className="form-input"
              name="id"
              onChange={handleChange}
              ref={register({
                required: true,
                pattern: {
                  // TODO: handle /
                  // eslint-disable-next-line no-useless-escape
                  value: /^[\w\.\-_]{3,}@[a-zA-Z]{3,}/,
                  message: 'Invalid UPI',
                },
              })}
            />
            <p className="form-error absolute">{error || (errors.id && errors.id.message)}</p>
          </div>
          <div className="info-column">
            <input
              className="form-btn"
              type="submit"
              value={`Pay ${parsePrice({ price: amount + internetCharges })}`}
            />
          </div>
        </div>
        <p className="small mt16">UPI Address is a unique payment address that is linked to a bank account.</p>
      </form>
    </div>
  );
};

export default Form;
